/**
 * Unknown Callers page — lists unique phone numbers that called in
 * but have no lead created in the system. Admin/manager only.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, UserPlus, Check, X } from 'lucide-react'
import { voipApi } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import MobileCard from '../../components/ui/MobileCard'
import { fmtDateTime } from '../../utils/date'

export default function UnknownCallersPage() {
  const authUser = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const [creatingFor, setCreatingFor] = useState<string | null>(null)
  const [leadName, setLeadName] = useState('')
  const [leadCompany, setLeadCompany] = useState('')
  const [leadEmail, setLeadEmail] = useState('')
  const [createdNumbers, setCreatedNumbers] = useState<Set<string>>(new Set())

  if (authUser?.role !== 'admin' && authUser?.role !== 'manager') {
    return <div className="p-8 text-center text-gray-500">Admin or manager access required.</div>
  }

  const { data, isLoading } = useQuery({
    queryKey: ['unknown-callers'],
    queryFn: () => voipApi.unknownCallers(),
  })

  const callers = data?.data || []

  // Find the most recent inbound call for this number to use as voipCallId
  const createLeadMutation = useMutation({
    mutationFn: async ({ phoneNumber, name, company, email }: { phoneNumber: string; name: string; company?: string; email?: string }) => {
      // Get the most recent call from this number
      const callsRes = await voipApi.list({ limit: '1', status: 'completed' })
      const call = (callsRes.calls || []).find((c: any) => c.from_number === phoneNumber && !c.lead_id)
      if (!call) throw new Error('No call found for this number')
      return voipApi.createLeadFromCall(call.id, { name, ...(company ? { company } : {}), ...(email ? { email } : {}) })
    },
    onSuccess: (_res, variables) => {
      setCreatedNumbers((prev) => new Set(prev).add(variables.phoneNumber))
      setCreatingFor(null)
      setLeadName('')
      setLeadCompany('')
      setLeadEmail('')
      queryClient.invalidateQueries({ queryKey: ['unknown-callers'] })
    },
  })

  const formatPhone = (num: string) => num.startsWith('+') ? num : `+${num}`

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unknown Callers</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Phone numbers that called in but have no lead in the system. Review and create leads as needed.
          </p>
        </div>
        <span className="text-sm text-gray-400">{callers.length} number{callers.length !== 1 ? 's' : ''}</span>
      </div>

      {isLoading && (
        <LoadingState message="Loading unknown callers..." />
      )}

      {!isLoading && callers.length === 0 && (
        <EmptyState
          icon={Phone}
          title="No unknown callers"
          description="All inbound callers are linked to leads. Great job keeping the CRM clean!"
        />
      )}

      {!isLoading && callers.length > 0 && (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {callers.map((caller) => {
              const isCreated = createdNumbers.has(caller.phone_number)
              const isCreating = creatingFor === caller.phone_number

              return (
                <MobileCard key={caller.phone_number}>
                  <p className="font-mono font-semibold text-gray-900 text-base mb-2">
                    {formatPhone(caller.phone_number)}
                  </p>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 text-xs mb-2">
                    <span className="text-gray-600">
                      <span className="font-medium text-gray-900">{caller.call_count}</span> calls
                    </span>
                    <span className="text-green-600">
                      <span className="font-medium">{caller.connected_count}</span> connected
                    </span>
                    <span className={caller.missed_count > 0 ? 'text-red-600' : 'text-gray-400'}>
                      <span className="font-medium">{caller.missed_count}</span> missed
                    </span>
                  </div>

                  {/* Last call date */}
                  <p className="text-xs text-gray-500 mb-3">
                    Last call: {caller.last_call_at ? fmtDateTime(caller.last_call_at) : '--'}
                  </p>

                  {/* Action area */}
                  {isCreated ? (
                    <div className="flex items-center justify-center gap-1.5 text-green-600 text-sm font-medium py-2">
                      <Check size={14} /> Lead Created
                    </div>
                  ) : isCreating ? (
                    <div className="space-y-2 pt-1">
                      <input
                        type="text"
                        value={leadName}
                        onChange={(e) => setLeadName(e.target.value)}
                        placeholder="Name *"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        autoFocus
                      />
                      <input
                        type="text"
                        value={leadCompany}
                        onChange={(e) => setLeadCompany(e.target.value)}
                        placeholder="Company"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="email"
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        placeholder="Email"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => createLeadMutation.mutate({
                            phoneNumber: caller.phone_number,
                            name: leadName,
                            company: leadCompany || undefined,
                            email: leadEmail || undefined,
                          })}
                          disabled={!leadName.trim() || createLeadMutation.isPending}
                          className="flex-1 min-h-[44px] bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {createLeadMutation.isPending ? 'Saving...' : 'Save Lead'}
                        </button>
                        <button
                          onClick={() => { setCreatingFor(null); setLeadName(''); setLeadCompany(''); setLeadEmail('') }}
                          className="min-h-[44px] px-4 text-gray-600 bg-gray-100 hover:bg-gray-200 text-sm font-medium rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                      {createLeadMutation.error && (
                        <p className="text-xs text-red-600">{(createLeadMutation.error as Error).message}</p>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setCreatingFor(caller.phone_number)}
                      className="w-full min-h-[44px] flex items-center justify-center gap-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 text-sm font-medium rounded-lg transition-colors"
                    >
                      <UserPlus size={14} /> Create Lead
                    </button>
                  )}
                </MobileCard>
              )
            })}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Total Calls</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Connected</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Missed</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">First Call</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Call</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {callers.map((caller) => {
                  const isCreated = createdNumbers.has(caller.phone_number)
                  const isCreating = creatingFor === caller.phone_number

                  return (
                    <tr key={caller.phone_number} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3">
                        <span className="font-mono font-medium text-gray-900">
                          {formatPhone(caller.phone_number)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-gray-900">{caller.call_count}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-green-600 font-medium">{caller.connected_count}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium ${caller.missed_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {caller.missed_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-gray-500">
                        {caller.first_call_at ? fmtDateTime(caller.first_call_at) : '--'}
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-gray-500">
                        {caller.last_call_at ? fmtDateTime(caller.last_call_at) : '--'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isCreated ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                            <Check size={12} /> Created
                          </span>
                        ) : isCreating ? (
                          <div className="text-left">
                            <div className="flex items-center gap-1 mb-1.5">
                              <input
                                type="text"
                                value={leadName}
                                onChange={(e) => setLeadName(e.target.value)}
                                placeholder="Name *"
                                className="w-28 px-2 py-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                                autoFocus
                              />
                              <input
                                type="text"
                                value={leadCompany}
                                onChange={(e) => setLeadCompany(e.target.value)}
                                placeholder="Company"
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <input
                                type="email"
                                value={leadEmail}
                                onChange={(e) => setLeadEmail(e.target.value)}
                                placeholder="Email"
                                className="w-36 px-2 py-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                              <button
                                onClick={() => createLeadMutation.mutate({
                                  phoneNumber: caller.phone_number,
                                  name: leadName,
                                  company: leadCompany || undefined,
                                  email: leadEmail || undefined,
                                })}
                                disabled={!leadName.trim() || createLeadMutation.isPending}
                                className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs rounded transition-colors"
                              >
                                {createLeadMutation.isPending ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={() => { setCreatingFor(null); setLeadName(''); setLeadCompany(''); setLeadEmail('') }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                <X size={12} />
                              </button>
                            </div>
                            {createLeadMutation.error && (
                              <p className="text-[10px] text-red-600 mt-1">{(createLeadMutation.error as Error).message}</p>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => setCreatingFor(caller.phone_number)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-medium rounded-lg transition-colors"
                          >
                            <UserPlus size={12} /> Create Lead
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
